import FinancialChart from "components/chart/FinancialChart";
import { Box, HStack, Typography } from "components/ui";
import { LinearGradient } from "expo-linear-gradient";
import { BarChart } from "lucide-react-native";
import React from "react";
import { Easing, ViewStyle } from "react-native";
import Animated, { interpolate, useAnimatedStyle, useSharedValue, withRepeat, withTiming } from "react-native-reanimated";
import { useGetDashboardSpendQuery } from "store/abcoApi";
import { useIsMobile } from "utils/hooks/useIsMobile";

const SkeletonLoader = () => {
	const isMobile = useIsMobile();
	const shimmerValue = useSharedValue(0);
	const pulseValue = useSharedValue(0);

	React.useEffect(() => {
		shimmerValue.value = withRepeat(withTiming(1, { duration: 1800, easing: Easing.inOut(Easing.ease) }), -1, true);
		pulseValue.value = withRepeat(withTiming(1, { duration: 1200, easing: Easing.inOut(Easing.ease) }), -1, true);
	}, []);

	const gradientStyle = useAnimatedStyle(() => ({
		position: "absolute",
		top: 0,
		bottom: 0,
		left: 0,
		right: 0,
		transform: [{ translateX: interpolate(shimmerValue.value, [0, 1], [-300, 300]) }],
	}));

	const pulseStyle = useAnimatedStyle(() => ({
		opacity: 0.3 + pulseValue.value * 0.4,
		backgroundColor: "#e0e0e0",
	}));

	interface ShimmerBoxProps {
		width: ViewStyle["width"];
		height: ViewStyle["height"];
		borderRadius?: number;
	}

	const ShimmerBox = ({ width, height, borderRadius = 8 }: ShimmerBoxProps) => (
		<Box height={height} width={width} borderRadius={borderRadius} overflow="hidden">
			<Animated.View style={[pulseStyle, { position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }]} />
			<Animated.View style={gradientStyle}>
				<LinearGradient colors={["rgba(255,255,255,0)", "rgba(255,255,255,0.8)", "rgba(255,255,255,0)"]} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }} style={{ width: 300, height: "100%" }} />
			</Animated.View>
		</Box>
	);

	return (
		<Box padding={isMobile ? 16 : 28}>
			<Box alignItems="center" flexDirection="row" mb={isMobile ? 16 : 28}>
				<ShimmerBox width={200} height={32} />
				{!isMobile && (
					<Box ml="auto" flexDirection="row" gap={16}>
						<ShimmerBox width={250} height={40} />
						<ShimmerBox width={250} height={40} />
					</Box>
				)}
			</Box>
			<Box bg="$basicwhite" borderRadius={12} padding={16} overflow="hidden">
				<ShimmerBox width="100%" height={isMobile ? 300 : 400} borderRadius={12} />
			</Box>
		</Box>
	);
};

const SpendSummary = () => {
	const { data: spend, isLoading: spendIsLoading, isFetching: spendsIsFetching } = useGetDashboardSpendQuery({});
	const isMobile = useIsMobile();

	if (spendIsLoading || spendsIsFetching) {
		return <SkeletonLoader />;
	}

	return (
		<Box>
			<Box padding={isMobile ? 16 : 28}>
				<HStack gap={16} alignItems="center" flexDirection="row">
					<BarChart size={48} />
					<Typography.H2 sx={{ marginRight: "auto" }}>Spend Summary</Typography.H2>
				</HStack>
				<Box py={28}>
					<Box bg="$basicwhite" borderRadius={12} padding={16} flex={2}>
						<FinancialChart spendData={spend} />
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default SpendSummary;
